<template>
  <div class="w-full min-h-screen flex flex-col">
    <MainNav :inverted="true" />
    <section class="w-full relative">
      <img
        src="/image/83islands-yasur.jpg"
        alt="Restless Earth"
        class="w-full h-[800px] object-cover object-[bottom_center] md:object-center"
      />

      <!-- Text Overlay -->
      <div class="mx-auto absolute mt-36 inset-0 z-20 w-full p-4 lg:p-36 flex flex-col items-center lg:items-start pointer-events-none">
        <h1 class="text-white font-maax text-4xl uppercase lg:text-6xl font-bold text-center lg:text-left max-w-3xl leading-tight drop-shadow-[0_5px_5px_rgba(0,0,0,0.3)] pointer-events-auto">
          Restless Earth
        </h1>
        <p class="text-white font-maax text-xl lg:text-2xl mt-4 text-center lg:text-left max-w-3xl drop-shadow-[0_5px_5px_rgba(0,0,0,0.3)] pointer-events-auto">
          Volcanic forces and seismic energy that define our islands.
        </p>
      </div>

      <!-- Double Wave -->
      <div class="absolute -bottom-1 left-0 right-0 w-full overflow-hidden">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="w-full h-20 text-white">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
                fill="currentColor" class="opacity-25"/>
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
                fill="currentColor"/>
        </svg>
      </div>
    </section>

    <main class="flex-grow w-full bg-white">
      <div class="container mx-auto p-4 lg:p-8">
        <!-- Intro -->
        <div class="mb-12 mt-12">
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            Vanuatu is breathtaking—for its beauty and its power. The same forces that create fertile soils, dramatic coastlines, and rich marine life also make these islands some of the most exposed in the world to cyclones, earthquakes, tsunamis, and volcanic eruptions.
          </p>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            This is the story of a restless earth—and of the communities who have learned to live with it.
          </p>
        </div>

        <!-- Why Vanuatu Is So Exposed -->
        <div class="mb-12">
          <h2 class="text-3xl font-maax font-bold uppercase text-black mb-4">Why Vanuatu Is So Exposed</h2>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            Vanuatu lies where the Indo-Australian plate dives beneath the Pacific plate, part of the famous Pacific "Ring of Fire". Deep offshore trenches, shifting seabeds and a rising island arc make the whole region geologically active.
          </p>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            At the same time, the archipelago sits in a corridor of warm ocean where tropical cyclones regularly form and pass through during the southern summer. Together, this means Vanuatu faces:
          </p>
          <ul class="list-disc list-inside font-maax text-black text-xl leading-relaxed mb-4 space-y-2">
            <li>Frequent earthquakes, including occasional major events</li>
            <li>The possibility of tsunamis generated close to shore</li>
            <li>Active volcanoes, both on land and beneath the sea</li>
            <li>Regular cyclones and tropical storms from November to April</li>
          </ul>
        </div>

        <!-- Cyclones -->
        <div class="mb-12">
          <h2 class="text-3xl font-maax font-bold uppercase text-black mb-4">Cyclones: Season of Wind and Water</h2>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            From 1 November to 30 April, Vanuatu enters its cyclone season. During these months, warm ocean water and unstable air can spin up powerful rotating storms.
          </p>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            Over the decades, the islands have seen cyclones of every category, from moderate storms to devastating systems that reshaped coastlines, damaged roads, destroyed gardens, and tested communities across the country. Some of the most significant in recent years include:
          </p>
          <ul class="list-disc list-inside font-maax text-black text-xl leading-relaxed mb-4 space-y-2">
            <li><strong>Cyclone Pam (2015)</strong> — One of the most powerful storms ever recorded in the South Pacific, causing widespread destruction across the archipelago.</li>
            <li><strong>Cyclone Harold (2020)</strong> — A Category 5 system that struck during the early days of the COVID-19 pandemic, compounding challenges for recovery efforts.</li>
            <li><strong>Cyclones Judy and Kevin (2023)</strong> — An unprecedented double strike within days of each other in late February and early March, with both systems bringing winds up to 183 km/h and causing catastrophic damage.</li>
          </ul>
        </div>

        <!-- Earthquakes & Tsunamis -->
        <div class="mb-12">
          <h2 class="text-3xl font-maax font-bold uppercase text-black mb-4">Earthquakes & Tsunamis</h2>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            Because the islands sit right above a subduction zone, earthquakes are a regular part of life. Each year, thousands of quakes are recorded along the arc, from small tremors to large events capable of damaging infrastructure and triggering landslides.
          </p>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            Some shallow offshore earthquakes can generate local tsunamis. These waves may only take 10–20 minutes to reach nearby coasts, leaving very little time to react. Historical tsunamis have struck parts of Ambrym, Pentecost, Tanna and other islands, with run-up heights of several metres in some places.
          </p>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            Because local tsunamis happen so fast, the first warning is often the shaking itself. If the ground shakes strongly and people are near the sea, the safest response is simple: go inland and uphill immediately, and wait for official advice.
          </p>
        </div>

        <!-- Volcanoes -->
        <div class="mb-12">
          <h2 class="text-3xl font-maax font-bold uppercase text-black mb-4">Volcanoes: Mountains of Fire</h2>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            Vanuatu's volcanoes are some of the most dramatic on earth.
          </p>
          <ul class="list-disc list-inside font-maax text-black text-xl leading-relaxed mb-4 space-y-2">
            <li>Yasur on Tanna is famous for its near-continuous strombolian eruptions, throwing glowing lava into the sky.</li>
            <li>Ambrym, Lopevi and Ambae have all produced ash plumes, gas emissions, and lava flows, with periods of calm and sudden activity.</li>
            <li>Under the sea, submarine volcanoes occasionally colour the water or push pumice and steam to the surface.</li>
          </ul>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            These "fire mountains" can threaten villages with ash, gas, or lahars—but they also create incredibly fertile soils, renew landscapes, and anchor traditional stories that give meaning to life on the islands.
          </p>
        </div>

        <!-- From Risk to Resilience -->
        <div class="mb-12">
          <h2 class="text-3xl font-maax font-bold uppercase text-black mb-4">From Risk to Resilience</h2>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            For Ni-Vanuatu communities, natural hazards are not just "disasters"—they're part of the relationship with the land and sea. Over generations, people have adapted by:
          </p>
          <ul class="list-disc list-inside font-maax text-black text-xl leading-relaxed mb-4 space-y-2">
            <li>Building with flexible materials that can be repaired or rebuilt</li>
            <li>Planting gardens in different zones to spread climate and hazard risk</li>
            <li>Listening to traditional knowledge and modern science side by side</li>
            <li>Teaching children how to respond to cyclones, earthquakes and tsunamis</li>
          </ul>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            At 83 Islands Distillery, we're deeply aware that every crop we use, every bottle we fill, exists thanks to this delicate balance between risk and renewal. Our rum honours the growers who rebuild after a storm, the villages that move back from the shore after a big wave, and the families who watch the volcano glow at night and still plant their gardens at dawn.
          </p>
          <p class="font-maax text-black text-xl leading-relaxed mb-4">
            When you explore this restless earth with us, you're not just learning about hazards—you're discovering the courage, creativity, and community that define Vanuatu, and that flow through every sip of 83 Islands Rum.
          </p>
        </div>

        <!-- Data Tables Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <!-- Cyclones Table -->
          <div id="cyclones" class="bg-gray-50 rounded-lg p-6">
            <h2 class="text-2xl font-maax font-bold uppercase text-black mb-2">Cyclones Since We Opened</h2>
            <p class="font-maax text-gray-700 text-base leading-relaxed mb-4">
              Cyclones within 1000km of the distillery.
            </p>
            <div v-if="loadingCyclones" class="text-center py-8">
              <p class="font-maax text-gray-600">Loading cyclone data...</p>
            </div>
            <div v-else-if="cyclones.length === 0" class="text-center py-8">
              <p class="font-maax text-gray-600">No cyclone data available.</p>
            </div>
            <div v-else class="overflow-x-auto max-h-96 overflow-y-auto">
              <table class="w-full border-collapse text-sm">
                <thead class="sticky top-0">
                  <tr class="bg-gray-200">
                    <th class="font-maax font-bold text-left p-2 border-b-2 border-gray-400 text-black">Name</th>
                    <th class="font-maax font-bold text-left p-2 border-b-2 border-gray-400 text-black">Season</th>
                    <th class="font-maax font-bold text-left p-2 border-b-2 border-gray-400 text-black">Max Wind</th>
                    <th class="font-maax font-bold text-left p-2 border-b-2 border-gray-400 text-black">Impact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cyclone in cyclones" :key="cyclone._id" class="hover:bg-gray-100">
                    <td class="font-maax p-2 border-b border-gray-300 font-bold text-black">{{ cyclone.name }}</td>
                    <td class="font-maax p-2 border-b border-gray-300 text-gray-800">{{ cyclone.season }}</td>
                    <td class="font-maax p-2 border-b border-gray-300 text-gray-800">{{ formatWindSpeed(cyclone.maxWindSpeed) }}</td>
                    <td class="font-maax p-2 border-b border-gray-300">
                      <span v-if="cyclone.within300km" class="text-red-600 font-bold">Direct</span>
                      <span v-else class="text-gray-600">Regional</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Earthquakes Table -->
          <div id="earthquakes" class="bg-gray-50 rounded-lg p-6">
            <h2 class="text-2xl font-maax font-bold uppercase text-black mb-2">Earthquakes Since We Opened</h2>
            <p class="font-maax text-gray-700 text-base leading-relaxed mb-4">
              Magnitude 4+ seismic activity recorded near Port Vila.
            </p>
            <div v-if="loadingEarthquakes" class="text-center py-8">
              <p class="font-maax text-gray-600">Loading earthquake data...</p>
            </div>
            <div v-else-if="earthquakes.length === 0" class="text-center py-8">
              <p class="font-maax text-gray-600">No earthquake data available.</p>
            </div>
            <div v-else class="overflow-x-auto max-h-96 overflow-y-auto">
              <table class="w-full border-collapse text-sm">
                <thead class="sticky top-0">
                  <tr class="bg-gray-200">
                    <th class="font-maax font-bold text-left p-2 border-b-2 border-gray-400 text-black">Date</th>
                    <th class="font-maax font-bold text-left p-2 border-b-2 border-gray-400 text-black">Mag</th>
                    <th class="font-maax font-bold text-left p-2 border-b-2 border-gray-400 text-black">Depth</th>
                    <th class="font-maax font-bold text-left p-2 border-b-2 border-gray-400 text-black">Distance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="earthquake in earthquakes" :key="earthquake._id" class="hover:bg-gray-100">
                    <td class="font-maax p-2 border-b border-gray-300 text-gray-800">{{ formatDate(earthquake.time) }}</td>
                    <td class="font-maax p-2 border-b border-gray-300">
                      <span :class="getMagnitudeClass(earthquake.magnitude)">{{ earthquake.magnitude?.toFixed(1) }}</span>
                    </td>
                    <td class="font-maax p-2 border-b border-gray-300 text-gray-800">{{ earthquake.depth?.toFixed(1) }} km</td>
                    <td class="font-maax p-2 border-b border-gray-300 text-gray-800">{{ earthquake.distanceFromPortVila?.toFixed(1) }} km</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </main>

    <Footer class="relative" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import MainNav from '../components/MainNav.vue'
import Footer from '../components/Footer.vue'

// API setup
const api = axios.create({
  baseURL: import.meta.env.DEV ? '/api' : import.meta.env.API_URL || '/api',
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  },
  timeout: 10000
})

// Reactive state
const earthquakes = ref([])
const cyclones = ref([])
const loadingEarthquakes = ref(true)
const loadingCyclones = ref(true)

// Fetch earthquake data (magnitude 4+)
const fetchEarthquakes = async () => {
  try {
    const response = await api.get('/earthquakes', {
      params: { limit: 10000, minMagnitude: 4 }
    })
    if (response.data.success) {
      earthquakes.value = response.data.data
    }
  } catch (error) {
    console.error('Error fetching earthquakes:', error)
  } finally {
    loadingEarthquakes.value = false
  }
}

// Fetch cyclone data
const fetchCyclones = async () => {
  try {
    const response = await api.get('/cyclones', {
      params: { within1000km: 'true', limit: 10000 }
    })
    if (response.data.success) {
      cyclones.value = response.data.data
    }
  } catch (error) {
    console.error('Error fetching cyclones:', error)
  } finally {
    loadingCyclones.value = false
  }
}

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

// Format date range for cyclones
const formatDateRange = (startDate, endDate) => {
  if (!startDate) return '—'
  const start = new Date(startDate)
  const end = endDate ? new Date(endDate) : null
  const startStr = start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  const endStr = end ? end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''
  return end ? `${startStr} – ${endStr}` : startStr
}

// Format wind speed (convert from km/h if needed)
const formatWindSpeed = (speed) => {
  if (!speed) return '—'
  return `${Math.round(speed)} km/h`
}

// Get CSS class based on magnitude
const getMagnitudeClass = (magnitude) => {
  if (!magnitude) return ''
  if (magnitude >= 6) return 'text-red-600 font-bold'
  if (magnitude >= 5) return 'text-orange-500 font-bold'
  if (magnitude >= 4) return 'text-yellow-600 font-semibold'
  return ''
}

// Fetch data on mount
onMounted(() => {
  fetchEarthquakes()
  fetchCyclones()
})
</script>
